import { Link } from "react-router-dom";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import { certifications } from "../data/site";

const fourCs = [
  {
    key: "Cut",
    lead: "The most important C.",
    text: "Cut determines how a diamond returns light. A well-cut stone reflects light from one facet to another and out through the top, creating sparkle, fire and brilliance. Our diamonds are chosen at Excellent and Very Good cut grades.",
    scale: ["Excellent", "Very Good", "Good", "Fair", "Poor"],
    highlight: 0,
  },
  {
    key: "Colour",
    lead: "How white is white.",
    text: "Diamond colour is graded from D (completely colourless) to Z (light yellow). The less colour, the rarer and more valuable the stone. We focus on the near-colourless D–F range, so your diamond reads pure and icy white.",
    scale: ["D", "E", "F", "G", "H", "I", "J"],
    highlight: 1,
  },
  {
    key: "Clarity",
    lead: "Nature's fingerprint.",
    text: "Clarity measures the tiny natural inclusions and surface blemishes formed as a diamond grows. Graded from Flawless (FL) to Included (I), higher clarity means fewer, smaller inclusions. Our diamonds are typically VVS — eye-clean brilliance.",
    scale: ["FL", "IF", "VVS1", "VVS2", "VS1", "VS2", "SI1"],
    highlight: 2,
  },
  {
    key: "Carat",
    lead: "A measure of weight.",
    text: "Carat is the unit of a diamond's weight — one carat equals 0.2 grams. Larger diamonds are rarer, but two stones of equal carat can differ greatly in value depending on their cut, colour and clarity. We offer a full spectrum of carat sizes.",
    scale: ["0.30", "0.50", "0.70", "1.00", "1.50", "2.00", "3.00+"],
    highlight: 3,
  },
];

const qualityHighlights = [
  {
    label: "VVS Clarity",
    text: "Very, Very Slightly Included — inclusions so tiny they are difficult to see even under 10× magnification. The diamond appears flawless to the eye.",
  },
  {
    label: "E / F Colour",
    text: "Near-colourless grades at the top of the scale. E and F diamonds show no discernible colour and hold their icy-white look in every setting.",
  },
  {
    label: "Excellent Cut",
    text: "Precision faceting engineered for maximum light return — the difference between a diamond that shines and one that dazzles.",
  },
];

export default function Diamonds() {
  return (
    <>
      <PageHero
        eyebrow="Diamond Guide"
        title="Understanding your diamond"
        subtitle="Every diamond is graded on four characteristics — the 4Cs. Here's what they mean, the quality we stand behind, and how our diamonds are certified."
        image="/assets/cd/prod-ring-box.jpg"
      />

      {/* Intro */}
      <section className="py-20 md:py-24">
        <div className="container-luxe grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
          <Reveal className="overflow-hidden">
            <img
              src="/assets/cd/prod-neck-pear.jpg"
              alt="A Culture Diamond necklace"
              className="h-[380px] w-full object-cover sm:h-[480px] lg:h-[540px]"
            />
          </Reveal>
          <Reveal delay={150}>
            <p className="eyebrow mb-5">The 4Cs of Diamonds</p>
            <h2 className="section-title mb-8">
              Four ways to read a{" "}
              <span className="italic text-gold-dark">diamond</span>
            </h2>
            <div className="space-y-5 font-sans text-base font-light leading-relaxed text-charcoal/70">
              <p>
                In the mid-20th century, the world's gemologists established a
                universal language for diamond quality — the 4Cs: Cut, Colour,
                Clarity and Carat. Together they describe a diamond's rarity,
                beauty and value.
              </p>
              <p>
                Understanding the 4Cs helps you compare diamonds with confidence
                and choose a stone that's right for you. At Culture Diamond Jewels,
                we hand-select diamonds that excel across all four.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* The 4Cs in detail */}
      <section className="border-y border-gold/15 bg-cream py-20 md:py-24">
        <div className="container-luxe space-y-6">
          {fourCs.map((c, i) => (
            <Reveal key={c.key} delay={i * 80}>
              <div className="grid gap-6 border border-gold/20 bg-white p-8 md:grid-cols-[200px_1fr] md:gap-10 md:p-10">
                <div>
                  <span className="font-serif text-6xl font-light text-gold/30">
                    0{i + 1}
                  </span>
                  <h3 className="mt-2 font-serif text-3xl font-light text-charcoal">
                    {c.key}
                  </h3>
                  <p className="mt-1 font-sans text-xs uppercase tracking-[0.2em] text-gold-dark">
                    {c.lead}
                  </p>
                </div>
                <div>
                  <p className="font-sans text-base font-light leading-relaxed text-charcoal/70">
                    {c.text}
                  </p>
                  <div className="mt-6 flex flex-wrap gap-2">
                    {c.scale.map((s, si) => (
                      <span
                        key={s}
                        className={`px-3 py-1.5 font-sans text-xs tracking-wide ${
                          si === c.highlight
                            ? "bg-gold text-white"
                            : "border border-charcoal/15 text-charcoal/55"
                        }`}
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                  <p className="mt-3 font-sans text-xs font-light italic text-charcoal/45">
                    Highlighted = the grade Culture Diamond typically selects.
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Our quality standard */}
      <section className="bg-charcoal py-20 md:py-28">
        <div className="container-luxe">
          <Reveal className="mb-14 text-center">
            <p className="eyebrow mb-5 text-gold-light">Our Standard</p>
            <h2 className="section-title text-cream">The quality we stand behind</h2>
            <p className="mx-auto mt-5 max-w-2xl font-sans text-sm font-light leading-relaxed text-cream/70">
              We don't chase the largest stone — we chase the most beautiful. That
              means selecting diamonds that consistently deliver on the qualities
              that matter most to the eye.
            </p>
          </Reveal>
          <div className="grid gap-8 md:grid-cols-3">
            {qualityHighlights.map((q, i) => (
              <Reveal key={q.label} delay={i * 110}>
                <div className="h-full border border-gold/25 p-8">
                  <h3 className="font-serif text-2xl font-light text-gold-light">
                    {q.label}
                  </h3>
                  <div className="my-4 h-px w-12 bg-gold/50" />
                  <p className="font-sans text-sm font-light leading-relaxed text-cream/65">
                    {q.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Lab-grown vs natural */}
      <section className="py-20 md:py-24">
        <div className="container-luxe grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
          <Reveal delay={150} className="order-2 lg:order-1">
            <p className="eyebrow mb-5">Natural & Lab-Grown</p>
            <h2 className="section-title mb-8">
              Two origins, one{" "}
              <span className="italic text-gold-dark">brilliance</span>
            </h2>
            <div className="space-y-5 font-sans text-base font-light leading-relaxed text-charcoal/70">
              <p>
                <span className="font-medium text-charcoal">Natural diamonds</span>{" "}
                are formed deep within the earth over billions of years — rare,
                storied and enduring.
              </p>
              <p>
                <span className="font-medium text-charcoal">Lab-grown diamonds</span>{" "}
                are created in controlled conditions that replicate nature. They are
                chemically, physically and optically identical to mined diamonds —
                graded on the very same 4Cs, at a more accessible price.
              </p>
              <p>
                We also craft{" "}
                <span className="font-medium text-charcoal">premium CZ jewellery</span>{" "}
                for the look of luxury every day. Whichever you choose, the sparkle
                is real and the craftsmanship is ours.
              </p>
            </div>
          </Reveal>
          <Reveal className="order-1 overflow-hidden lg:order-2">
            <img
              src="/assets/cd/prod-tennis-navy.jpg"
              alt="Diamond jewellery by Culture Diamond"
              className="h-[380px] w-full object-cover sm:h-[480px] lg:h-[540px]"
            />
          </Reveal>
        </div>
      </section>

      {/* Certification */}
      <section className="border-t border-gold/15 bg-cream py-20 md:py-24">
        <div className="container-luxe">
          <Reveal className="mb-14 text-center">
            <p className="eyebrow mb-5">Certification</p>
            <h2 className="section-title">Independently certified</h2>
            <p className="mx-auto mt-5 max-w-2xl font-sans text-sm font-light leading-relaxed text-charcoal/60">
              Every certified diamond we sell is graded by an internationally
              recognised gemological laboratory, so its quality is documented and
              guaranteed — never just our word.
            </p>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-3">
            {certifications.map((cert, i) => (
              <Reveal key={cert.code} delay={i * 110}>
                <div className="h-full border border-gold/25 bg-white p-8 text-center">
                  <p className="font-serif text-4xl font-light text-gold-dark">
                    {cert.code}
                  </p>
                  <div className="mx-auto my-4 h-px w-12 bg-gold/50" />
                  <p className="font-sans text-sm font-light leading-relaxed text-charcoal/65">
                    {cert.name}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-14 text-center">
            <Link to="/collections" className="btn-gold">
              Explore the Collections
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
